// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Local call recorder — the artefact. Mixes every audio stream (yours + every
 * peer's) into one track via a Web Audio MediaStreamDestination, then encodes it
 * to a compact Opus/WebM audio file with MediaRecorder, entirely on-device. The
 * recording is a local file; it is never uploaded. Peers are told over the data
 * channel that recording is on.
 */

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

export function pickRecordMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* keep trying */
    }
  }
  return '';
}

export function recorderSupported(): boolean {
  return typeof MediaRecorder !== 'undefined' && pickRecordMime() !== null;
}

/** File extension implied by a recorder mime type. */
export function extForMime(mime: string): string {
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4')) return 'm4a';
  return 'webm';
}

export class CallRecorder {
  private rec: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private audioDest: MediaStreamAudioDestinationNode | null = null;
  private connected = new Map<MediaStream, MediaStreamAudioSourceNode>();
  mime = '';

  constructor(private audioCtx: AudioContext) {}

  /** Begin recording, mixing the given audio streams. */
  start(initialAudio: MediaStream[]): void {
    this.audioDest = this.audioCtx.createMediaStreamDestination();
    this.syncAudio(initialAudio);

    const mixed = new MediaStream();
    for (const t of this.audioDest.stream.getAudioTracks()) mixed.addTrack(t);

    const mime = pickRecordMime() ?? '';
    this.mime = mime || 'audio/webm';
    this.rec = mime ? new MediaRecorder(mixed, { mimeType: mime }) : new MediaRecorder(mixed);
    this.chunks = [];
    this.rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.rec.start(1000);
  }

  /** Reconcile which audio streams feed the mixdown (people join/leave mid-record). */
  syncAudio(streams: MediaStream[]): void {
    if (!this.audioDest) return;
    const wanted = new Set(streams.filter((s) => s.getAudioTracks().length > 0));
    for (const [stream, node] of this.connected) {
      if (!wanted.has(stream)) {
        try {
          node.disconnect();
        } catch {
          /* gone */
        }
        this.connected.delete(stream);
      }
    }
    for (const stream of wanted) {
      if (this.connected.has(stream)) continue;
      try {
        const node = this.audioCtx.createMediaStreamSource(stream);
        node.connect(this.audioDest);
        this.connected.set(stream, node);
      } catch {
        /* stream not connectable */
      }
    }
  }

  async stop(): Promise<Blob> {
    const rec = this.rec;
    const blob = await new Promise<Blob>((resolve) => {
      if (!rec) {
        resolve(new Blob(this.chunks, { type: this.mime }));
        return;
      }
      rec.onstop = () => resolve(new Blob(this.chunks, { type: this.mime }));
      try {
        rec.stop();
      } catch {
        resolve(new Blob(this.chunks, { type: this.mime }));
      }
    });
    for (const [, node] of this.connected) {
      try {
        node.disconnect();
      } catch {
        /* gone */
      }
    }
    this.connected.clear();
    this.audioDest = null;
    this.rec = null;
    return blob;
  }
}

export function fmtDur(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
